import { NgClass, NgTemplateOutlet, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, HostListener, Output, Input, Component, ViewChild, ViewEncapsulation, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { MatListItem, MatNavList, MatListModule } from '@angular/material/list';
import * as i1 from '@angular/router';
import { RouterLink, NavigationEnd, RouterModule } from '@angular/router';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import { WindmillTooltipDirective, WindmillTooltipModule } from '@windmill/ng-windmill/tooltip';
import { Subject, Subscription } from 'rxjs';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatSidenav, MatSidenavContainer, MatSidenavContent, MatSidenavModule } from '@angular/material/sidenav';
import * as i2 from '@angular/cdk/layout';
import { LayoutModule } from '@angular/cdk/layout';
import * as i3 from '@windmill/ng-windmill/shared';
import { CentricButtonModule } from '@windmill/ng-windmill/button';

class SidenavService {
    itemClickedSubscription = new Subject();
    itemClicked() {
        if (this.itemClickedSubscription) {
            this.itemClickedSubscription.next();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SidenavService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SidenavService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SidenavService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const LEFT_PADDING = 24;
const windmillComponents = [CentricIcon2Component, WindmillTooltipDirective];
/**
 * To use this component in a module, you can import the `CentricSidenavModule`:
 * @example
 * ```typescript
 * import { CentricSidenavModule } from '@windmill/ng-windmill/sidenav';
 * ```
 *
 * To use the standalone component directly, you can import `SidenavItemComponent`:
 * @example
 * ```typescript
 * import { SidenavItemComponent } from '@windmill/ng-windmill/sidenav';
 * ```
 */
class SidenavItemComponent {
    router;
    sidenavService;
    /**
     * Is the custom route
     */
    menuItem;
    /**
     * The route's path
     */
    routerPath;
    /**
     * The depth of the item
     */
    depth;
    /**
     * Event emitted when the expanded item status is changed
     */
    expandedItemChange = new EventEmitter();
    lockedTooltipPosition = 'right';
    get leftPadding() {
        return `${LEFT_PADDING * this.depth}px`;
    }
    get expandableButtonClassList() {
        let classList = '';
        if (this.menuItem.isActive) {
            classList += this._concatenateClass(classList, 'expanded-parent');
        }
        if (this.hasActiveChildren(this.menuItem)) {
            classList += this._concatenateClass(classList, 'active-parent');
        }
        else if (this._isParentSelected()) {
            classList += this._concatenateClass(classList, 'selected-parent');
        }
        return classList ? classList : null;
    }
    get isItemLocked() {
        return Boolean(this.menuItem.isLocked || this.menuItem.hasLockedAscendant || this.menuItem.hasLockedAllDescendants);
    }
    get isLockedWithTooltip() {
        return Boolean(this.isItemLocked && this.menuItem.lockedTooltipMessage);
    }
    get lockedTooltipMessageValue() {
        return String(this.menuItem.lockedTooltipMessage);
    }
    get lockedTooltipColorValue() {
        if (this.isItemLocked &&
            this.menuItem.lockedTooltipMessage &&
            (typeof this.menuItem.lockedTooltipColor === 'undefined' ||
                !['neutral', 'surface', 'theme'].includes(this.menuItem.lockedTooltipColor))) {
            return 'theme';
        }
        if (typeof this.menuItem.lockedTooltipColor === 'undefined') {
            return 'theme';
        }
        return this.menuItem.lockedTooltipColor;
    }
    constructor(router, sidenavService) {
        this.router = router;
        this.sidenavService = sidenavService;
    }
    onKeydownChange(event) {
        if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
        }
    }
    onKeyboardChange(event) {
        switch (event.key) {
            case ' ':
            case 'Spacebar': {
                this.toggleSelectedItem();
                event.preventDefault();
                event.stopPropagation();
                break;
            }
            default:
        }
    }
    hasActiveChildren(menuItem) {
        if (!menuItem.children) {
            return false;
        }
        const activeChildren = menuItem.children.filter(child => {
            if (child.children) {
                return this.hasActiveChildren(child);
            }
            return child.isActive;
        }).length;
        return activeChildren > 0;
    }
    toggleSelectedItem() {
        if (this.menuItem) {
            this.menuItem.isActive = !this.menuItem.isActive;
            this.expandedItemChange.emit(this.menuItem);
        }
    }
    onItemClicked() {
        this.sidenavService.itemClicked();
    }
    getIconArrow(isActive) {
        return isActive ? 'caret-small-down_b' : 'caret-small-right_b';
    }
    _isParentSelected() {
        if (!this.menuItem.path) {
            return false;
        }
        const fullPath = `/${String(this.menuItem?.path)}`;
        return this.router.url === fullPath;
    }
    _concatenateClass(classList, className) {
        return classList ? ` ${className}` : className;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SidenavItemComponent, deps: [{ token: i1.Router }, { token: SidenavService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: SidenavItemComponent, isStandalone: true, selector: "centric-sidenav-item", inputs: { menuItem: "menuItem", routerPath: "routerPath", depth: "depth" }, outputs: { expandedItemChange: "expandedItemChange" }, host: { listeners: { "keydown": "onKeydownChange($event)" } }, ngImport: i0, template: "@if (menuItem.children) {\r\n  @if (isLockedWithTooltip) {\r\n    <button\r\n      mat-list-item\r\n      tabindex=\"0\"\r\n      [ngClass]=\"expandableButtonClassList\"\r\n      [class.has-locked-state]=\"isItemLocked\"\r\n      [style.paddingLeft]=\"leftPadding\"\r\n      [attr.data-testid]=\"menuItem.testId\"\r\n      [attr.aria-expanded]=\"menuItem.isActive\"\r\n      [attr.aria-current]=\"hasActiveChildren(menuItem) ? 'location' : ''\"\r\n      windmill-tooltip\r\n      [tooltipPosition]=\"lockedTooltipPosition\"\r\n      [tooltipText]=\"lockedTooltipMessageValue\"\r\n      [tooltipColor]=\"lockedTooltipColorValue\"\r\n      (click)=\"toggleSelectedItem()\"\r\n      (keydown)=\"onKeyboardChange($event)\">\r\n      <div class=\"arrow-container\">\r\n        <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n      </div>\r\n      <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n    </button>\r\n  } @else {\r\n    <button\r\n      mat-list-item\r\n      tabindex=\"0\"\r\n      [ngClass]=\"expandableButtonClassList\"\r\n      [class.has-locked-state]=\"isItemLocked\"\r\n      [style.paddingLeft]=\"leftPadding\"\r\n      [attr.data-testid]=\"menuItem.testId\"\r\n      [attr.aria-expanded]=\"menuItem.isActive\"\r\n      [attr.aria-current]=\"hasActiveChildren(menuItem) ? 'location' : ''\"\r\n      (click)=\"toggleSelectedItem()\"\r\n      (keydown)=\"onKeyboardChange($event)\">\r\n      <div class=\"arrow-container\">\r\n        <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n      </div>\r\n      <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n    </button>\r\n  }\r\n} @else {\r\n  @if (menuItem.externalUrl) {\r\n    @if (isLockedWithTooltip) {\r\n      <a\r\n        mat-list-item\r\n        rel=\"noopener\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [href]=\"menuItem.externalUrl\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [target]=\"menuItem.target ? menuItem.target : ''\"\r\n        windmill-tooltip\r\n        [tooltipPosition]=\"lockedTooltipPosition\"\r\n        [tooltipText]=\"lockedTooltipMessageValue\"\r\n        [tooltipColor]=\"lockedTooltipColorValue\">\r\n        <div class=\"arrow-container\"></div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    } @else {\r\n      <a\r\n        mat-list-item\r\n        rel=\"noopener\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [href]=\"menuItem.externalUrl\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [target]=\"menuItem.target ? menuItem.target : ''\">\r\n        <div class=\"arrow-container\"></div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    }\r\n  } @else {\r\n    @if (isLockedWithTooltip) {\r\n      <a\r\n        mat-list-item\r\n        [ngClass]=\"{ 'active-child': menuItem.isActive }\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [routerLink]=\"menuItem.routerLink ? menuItem.routerLink : routerPath\"\r\n        [queryParams]=\"menuItem.queryParams\"\r\n        [queryParamsHandling]=\"menuItem.queryParamsHandling\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [attr.aria-current]=\"menuItem.isActive ? 'page' : ''\"\r\n        windmill-tooltip\r\n        [tooltipPosition]=\"lockedTooltipPosition\"\r\n        [tooltipText]=\"lockedTooltipMessageValue\"\r\n        [tooltipColor]=\"lockedTooltipColorValue\"\r\n        (click)=\"onItemClicked()\">\r\n        <div class=\"arrow-container\">\r\n          <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n        </div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    } @else {\r\n      <a\r\n        mat-list-item\r\n        [ngClass]=\"{ 'active-child': menuItem.isActive }\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [routerLink]=\"menuItem.routerLink ? menuItem.routerLink : routerPath\"\r\n        [queryParams]=\"menuItem.queryParams\"\r\n        [queryParamsHandling]=\"menuItem.queryParamsHandling\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [attr.aria-current]=\"menuItem.isActive ? 'page' : ''\"\r\n        (click)=\"onItemClicked()\">\r\n        <div class=\"arrow-container\">\r\n          <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n        </div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    }\r\n  }\r\n}\r\n\r\n@if (menuItem.children && menuItem.isActive) {\r\n  <div class=\"submenu\">\r\n    @for (submenuItem of menuItem.children; track submenuItem) {\r\n      <centric-sidenav-item\r\n        [menuItem]=\"submenuItem\"\r\n        [routerPath]=\"routerPath + '/' + submenuItem.path\"\r\n        [depth]=\"depth + 1\"></centric-sidenav-item>\r\n    }\r\n  </div>\r\n}\r\n\r\n<ng-template #menuItemContent>\r\n  @if (menuItem.icon) {\r\n    <centric-icon2\r\n      class=\"menu-item-icon module-icon\"\r\n      [size]=\"'24'\"\r\n      [svgIcon]=\"menuItem.icon\"></centric-icon2>\r\n  }\r\n  <span mat-list-text>\r\n    <span class=\"menu-item-text\">{{ menuItem.name }}</span>\r\n    @if (isItemLocked) {\r\n      <centric-icon2\r\n        class=\"lock-icon\"\r\n        [svgIcon]=\"'lock_b'\"\r\n        [size]=\"'16'\"></centric-icon2>\r\n    }\r\n  </span>\r\n</ng-template>\r\n\r\n<ng-template #arrows>\r\n  @if (menuItem.children) {\r\n    <centric-icon2\r\n      class=\"menu-item-icon\"\r\n      [svgIcon]=\"getIconArrow(menuItem.isActive)\"\r\n      [size]=\"'20'\">\r\n    </centric-icon2>\r\n  }\r\n</ng-template>\r\n", dependencies: [{ kind: "component", type: SidenavItemComponent, selector: "centric-sidenav-item", inputs: ["menuItem", "routerPath", "depth"], outputs: ["expandedItemChange"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: MatListItem, selector: "mat-list-item, a[mat-list-item], button[mat-list-item]", inputs: ["activated"], exportAs: ["matListItem"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }, { kind: "directive", type: WindmillTooltipDirective, selector: "[windmill-tooltip]", inputs: ["tooltipText", "tooltipPosition", "tooltipShowDelay", "tooltipHideDelay", "tooltipClassName", "tooltipColor", "isTooltipDisabled"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SidenavItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-sidenav-item', imports: [NgClass, NgTemplateOutlet, RouterLink, MatListItem, ...windmillComponents], standalone: true, template: "@if (menuItem.children) {\r\n  @if (isLockedWithTooltip) {\r\n    <button\r\n      mat-list-item\r\n      tabindex=\"0\"\r\n      [ngClass]=\"expandableButtonClassList\"\r\n      [class.has-locked-state]=\"isItemLocked\"\r\n      [style.paddingLeft]=\"leftPadding\"\r\n      [attr.data-testid]=\"menuItem.testId\"\r\n      [attr.aria-expanded]=\"menuItem.isActive\"\r\n      [attr.aria-current]=\"hasActiveChildren(menuItem) ? 'location' : ''\"\r\n      windmill-tooltip\r\n      [tooltipPosition]=\"lockedTooltipPosition\"\r\n      [tooltipText]=\"lockedTooltipMessageValue\"\r\n      [tooltipColor]=\"lockedTooltipColorValue\"\r\n      (click)=\"toggleSelectedItem()\"\r\n      (keydown)=\"onKeyboardChange($event)\">\r\n      <div class=\"arrow-container\">\r\n        <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n      </div>\r\n      <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n    </button>\r\n  } @else {\r\n    <button\r\n      mat-list-item\r\n      tabindex=\"0\"\r\n      [ngClass]=\"expandableButtonClassList\"\r\n      [class.has-locked-state]=\"isItemLocked\"\r\n      [style.paddingLeft]=\"leftPadding\"\r\n      [attr.data-testid]=\"menuItem.testId\"\r\n      [attr.aria-expanded]=\"menuItem.isActive\"\r\n      [attr.aria-current]=\"hasActiveChildren(menuItem) ? 'location' : ''\"\r\n      (click)=\"toggleSelectedItem()\"\r\n      (keydown)=\"onKeyboardChange($event)\">\r\n      <div class=\"arrow-container\">\r\n        <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n      </div>\r\n      <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n    </button>\r\n  }\r\n} @else {\r\n  @if (menuItem.externalUrl) {\r\n    @if (isLockedWithTooltip) {\r\n      <a\r\n        mat-list-item\r\n        rel=\"noopener\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [href]=\"menuItem.externalUrl\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [target]=\"menuItem.target ? menuItem.target : ''\"\r\n        windmill-tooltip\r\n        [tooltipPosition]=\"lockedTooltipPosition\"\r\n        [tooltipText]=\"lockedTooltipMessageValue\"\r\n        [tooltipColor]=\"lockedTooltipColorValue\">\r\n        <div class=\"arrow-container\"></div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    } @else {\r\n      <a\r\n        mat-list-item\r\n        rel=\"noopener\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [href]=\"menuItem.externalUrl\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [target]=\"menuItem.target ? menuItem.target : ''\">\r\n        <div class=\"arrow-container\"></div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    }\r\n  } @else {\r\n    @if (isLockedWithTooltip) {\r\n      <a\r\n        mat-list-item\r\n        [ngClass]=\"{ 'active-child': menuItem.isActive }\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [routerLink]=\"menuItem.routerLink ? menuItem.routerLink : routerPath\"\r\n        [queryParams]=\"menuItem.queryParams\"\r\n        [queryParamsHandling]=\"menuItem.queryParamsHandling\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [attr.aria-current]=\"menuItem.isActive ? 'page' : ''\"\r\n        windmill-tooltip\r\n        [tooltipPosition]=\"lockedTooltipPosition\"\r\n        [tooltipText]=\"lockedTooltipMessageValue\"\r\n        [tooltipColor]=\"lockedTooltipColorValue\"\r\n        (click)=\"onItemClicked()\">\r\n        <div class=\"arrow-container\">\r\n          <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n        </div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    } @else {\r\n      <a\r\n        mat-list-item\r\n        [ngClass]=\"{ 'active-child': menuItem.isActive }\"\r\n        [class.has-locked-state]=\"isItemLocked\"\r\n        [style.paddingLeft]=\"leftPadding\"\r\n        [routerLink]=\"menuItem.routerLink ? menuItem.routerLink : routerPath\"\r\n        [queryParams]=\"menuItem.queryParams\"\r\n        [queryParamsHandling]=\"menuItem.queryParamsHandling\"\r\n        [attr.data-testid]=\"menuItem.testId\"\r\n        [attr.aria-current]=\"menuItem.isActive ? 'page' : ''\"\r\n        (click)=\"onItemClicked()\">\r\n        <div class=\"arrow-container\">\r\n          <ng-container *ngTemplateOutlet=\"arrows\"></ng-container>\r\n        </div>\r\n        <ng-container *ngTemplateOutlet=\"menuItemContent\"></ng-container>\r\n      </a>\r\n    }\r\n  }\r\n}\r\n\r\n@if (menuItem.children && menuItem.isActive) {\r\n  <div class=\"submenu\">\r\n    @for (submenuItem of menuItem.children; track submenuItem) {\r\n      <centric-sidenav-item\r\n        [menuItem]=\"submenuItem\"\r\n        [routerPath]=\"routerPath + '/' + submenuItem.path\"\r\n        [depth]=\"depth + 1\"></centric-sidenav-item>\r\n    }\r\n  </div>\r\n}\r\n\r\n<ng-template #menuItemContent>\r\n  @if (menuItem.icon) {\r\n    <centric-icon2\r\n      class=\"menu-item-icon module-icon\"\r\n      [size]=\"'24'\"\r\n      [svgIcon]=\"menuItem.icon\"></centric-icon2>\r\n  }\r\n  <span mat-list-text>\r\n    <span class=\"menu-item-text\">{{ menuItem.name }}</span>\r\n    @if (isItemLocked) {\r\n      <centric-icon2\r\n        class=\"lock-icon\"\r\n        [svgIcon]=\"'lock_b'\"\r\n        [size]=\"'16'\"></centric-icon2>\r\n    }\r\n  </span>\r\n</ng-template>\r\n\r\n<ng-template #arrows>\r\n  @if (menuItem.children) {\r\n    <centric-icon2\r\n      class=\"menu-item-icon\"\r\n      [svgIcon]=\"getIconArrow(menuItem.isActive)\"\r\n      [size]=\"'20'\">\r\n    </centric-icon2>\r\n  }\r\n</ng-template>\r\n" }]
        }], ctorParameters: () => [{ type: i1.Router }, { type: SidenavService }], propDecorators: { menuItem: [{
                type: Input
            }], routerPath: [{
                type: Input
            }], depth: [{
                type: Input
            }], expandedItemChange: [{
                type: Output
            }], onKeydownChange: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const DEPTH = -1;
const materialComponents = [MatNavList, MatSidenav, MatSidenavContainer, MatSidenavContent];
/**
 * To use this component in a module, you can import the `CentricSidenavModule`:
 * @example
 * ```typescript
 * import { CentricSidenavModule } from '@windmill/ng-windmill/sidenav';
 * ```
 *
 * To use the standalone component directly, you can import `SidenavComponent`:
 * @example
 * ```typescript
 * import { SidenavComponent } from '@windmill/ng-windmill/sidenav';
 * ```
 */
class SidenavComponent {
    router;
    breakpointObserver;
    responsiveService;
    sidenavService;
    /**
     * Mode of the drawer; one of 'over', 'push' or 'side'
     * By default is 'side'
     */
    mode = 'side';
    /**
     * Can be used to set the state of the menu
     */
    opened = false;
    /**
     * Contains the application's routes
     */
    menuItems;
    /**
     * Implies scrolling to the top of the mat-sidenav-content container after navigation ends
     * By default is true
     */
    scrollToTop = true;
    /**
     * Automatically collapse all other sibling menu items, except for the currently expanded one
     * This only applies to the first level of menu items
     * By default is false
     */
    get autoCollapse() {
        return this._autoCollapse;
    }
    set autoCollapse(value) {
        this._autoCollapse = value;
        this.collapseFirstLevelMenuItems();
    }
    /**
     * Focus the first focusable element automatically when opened
     */
    get autoFocus() {
        const value = this._autoFocus;
        return value === null ? this.mode !== 'side' : value;
    }
    set autoFocus(value) {
        this._autoFocus = coerceBooleanProperty(value);
    }
    /**
     * The aria label attribute for floating close button
     */
    ariaLabelCloseButton;
    /**
     * Display on full screen on small breakpoints
     * By default is false
     */
    fullOverMode = false;
    /**
     * Event emitted when the drawer open state is changed.
     */
    openedChange = new EventEmitter();
    sidenavContainer;
    depth = DEPTH;
    routerSubscription = Subscription.EMPTY;
    buttonCloseFocused = false;
    _autoCollapse = false;
    _autoFocus = null;
    breakpointObserverSubscription;
    closedSubscription;
    isSmallBreakpoint = false;
    _descendants = [];
    constructor(router, breakpointObserver, responsiveService, sidenavService) {
        this.router = router;
        this.breakpointObserver = breakpointObserver;
        this.responsiveService = responsiveService;
        this.sidenavService = sidenavService;
        this.subscribeToRouterEvents();
    }
    ngOnInit() {
        this.populateWithAscendants();
        this.initializeRoutes();
        this.watchForSmallBreakpoint();
        this.subscribeToClosedEvent();
    }
    ngOnChanges(changes) {
        if (changes['menuItems']?.currentValue) {
            this.populateWithAscendants();
        }
    }
    ngOnDestroy() {
        this.routerSubscription.unsubscribe();
        if (this.breakpointObserverSubscription) {
            this.breakpointObserverSubscription.unsubscribe();
        }
        this.closedSubscription?.unsubscribe();
    }
    initializeRoutes() {
        const activeRoute = this.router.url.substr(1);
        const itemsUrl = activeRoute.split('/');
        itemsUrl[itemsUrl.length - 1] = itemsUrl[itemsUrl.length - 1].split(/[?#]/)[0];
        this.updateRoutes(itemsUrl);
    }
    subscribeToRouterEvents() {
        this.routerSubscription = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.setActiveItems(event);
                if (this.scrollToTop && this.sidenavContainer) {
                    this.sidenavContainer.scrollable.scrollTo({ top: 0 });
                }
            }
        });
    }
    populateWithAscendants() {
        this.menuItems?.forEach(menu => {
            menu.parent = null;
            if (menu.children) {
                this.createAscendants(menu.children, menu);
            }
        });
        this._updateLockedProperties(this.menuItems);
    }
    createAscendants(children, parent) {
        children.forEach(child => {
            child.parent = this.createAscendant(parent);
            if (child.children) {
                this.createAscendants(child.children, child);
            }
        });
    }
    setActiveItems(event) {
        const itemsUrl = this.splitUrl(event);
        itemsUrl[itemsUrl.length - 1] = itemsUrl[itemsUrl.length - 1].split(/[?#]/)[0];
        this.updateRoutes(itemsUrl);
    }
    updateRoutes(itemsUrl) {
        const currentItems = itemsUrl.filter(item => item !== '');
        const parentUrl = currentItems[0];
        if (this.routeExists(parentUrl)) {
            this.setActiveStatus(true, currentItems, this.menuItems);
        }
        else {
            const fullUrl = currentItems.join('/');
            this.setActiveStatus(false, currentItems, this.menuItems);
            this.activateChild(fullUrl);
        }
    }
    setActiveStatus(active, itemsUrl, menuItems) {
        menuItems?.forEach(menuItem => {
            if (menuItem.children || !menuItem.parent) {
                menuItem.isActive = this._isChildrenItemActive(active, itemsUrl, menuItem);
            }
            else {
                menuItem.isActive = this._isParentItemActive(active, itemsUrl, menuItem);
            }
            if (menuItem.children) {
                this.setActiveStatus(active, itemsUrl, menuItem.children);
            }
        });
    }
    activateChild(url) {
        this.menuItems?.forEach(menuItem => {
            if (menuItem.path === '' && menuItem.children) {
                this._activateChildren(menuItem, url);
            }
            else {
                this._activateItemWithFullPath(menuItem, url);
            }
        });
    }
    activateAscendants(child) {
        if (child.parent === null && typeof child.parent === 'undefined') {
            return;
        }
        this.menuItems?.forEach((menuItem) => {
            if (menuItem.name === child.parent?.name) {
                menuItem.isActive = true;
                if (menuItem.parent) {
                    this.activateAscendants(menuItem.parent);
                }
            }
        });
    }
    toggleMenu() {
        this.opened = !this.opened;
        this.openedChange.emit(this.opened);
    }
    onFocus() {
        this.buttonCloseFocused = true;
    }
    onBlur() {
        this.buttonCloseFocused = false;
    }
    onExpandedItemChange(emittedMenuItem) {
        if (this.autoCollapse) {
            this.collapseFirstLevelMenuItems(emittedMenuItem);
        }
    }
    subscribeToClosedEvent() {
        if (this.sidenavService.itemClickedSubscription) {
            this.closedSubscription = this.sidenavService.itemClickedSubscription.subscribe(() => {
                if (this.fullOverMode && this.isSmallBreakpoint) {
                    this.opened = false;
                    this.openedChange.emit(this.opened);
                }
            });
        }
    }
    watchForSmallBreakpoint() {
        this.breakpointObserverSubscription = this.breakpointObserver
            .observe([this.responsiveService.breakpoints.small])
            .subscribe((result) => {
            this.isSmallBreakpoint = result.matches;
        });
    }
    splitUrl(event) {
        if (event.url !== '/') {
            return event.url.split('/').filter(x => x !== '');
        }
        return event.urlAfterRedirects.split('/');
    }
    routeExists(url) {
        return this.menuItems?.find(x => x.path === url);
    }
    createAscendant(ascendant) {
        return { name: ascendant.name, path: ascendant.path, parent: ascendant.parent, isLocked: ascendant.isLocked };
    }
    collapseFirstLevelMenuItems(exceptionMenuItem) {
        this.menuItems?.forEach((menuItem) => {
            if (menuItem.children && (!exceptionMenuItem || exceptionMenuItem !== menuItem)) {
                menuItem.isActive = false;
            }
        });
    }
    _isChildrenItemActive(active, itemsUrl, menuItem) {
        return menuItem?.path && menuItem.path !== '' && itemsUrl.includes(menuItem.path) ? active : false;
    }
    _isParentItemActive(active, itemsUrl, menuItem) {
        return menuItem?.parent?.path &&
            menuItem?.path &&
            menuItem.path !== '' &&
            itemsUrl.includes(menuItem.path) &&
            itemsUrl.includes(menuItem.parent?.path)
            ? active
            : false;
    }
    _activateChildren(menuItem, url) {
        menuItem?.children?.forEach(child => {
            if (child?.path) {
                const escapedPath = child?.path.replace(/\//g, '\\/');
                const pattern = new RegExp(`^${escapedPath}(?:\/|$)`);
                if (pattern.test(url)) {
                    child.isActive = true;
                    this.activateAscendants(child);
                }
            }
        });
    }
    _activateItemWithFullPath(menuItem, url) {
        const fullPath = `/${url}`;
        if (fullPath === menuItem.path) {
            menuItem.isActive = true;
        }
    }
    _updateLockedProperties(menuItems) {
        this._clearLockedProperties(menuItems);
        this._updateHasLockedAscendant(menuItems);
        this._updateHasLockedAllDescendants(menuItems);
    }
    _clearLockedProperties(menuItems) {
        menuItems?.forEach(menuItem => {
            menuItem.hasLockedAscendant = false;
            menuItem.hasLockedAllDescendants = false;
            if (menuItem.children) {
                this._clearLockedProperties(menuItem.children);
            }
        });
    }
    _updateHasLockedAscendant(menuItems) {
        menuItems?.forEach(menuItem => {
            if (menuItem.parent?.isLocked || menuItem.parent?.parent?.isLocked) {
                menuItem.hasLockedAscendant = true;
            }
            else {
                menuItem.hasLockedAscendant = false;
            }
            if (menuItem.children) {
                this._updateHasLockedAscendant(menuItem.children);
            }
        });
    }
    _updateHasLockedAllDescendants(menuItems) {
        menuItems?.forEach(menuItem => {
            this._updateItem(menuItem);
        });
    }
    _updateItem(item) {
        this._descendants = [];
        this._getAllLeafNodes(item);
        item.hasLockedAllDescendants = this._descendants?.every(descendant => descendant !== item && (descendant.isLocked || descendant.hasLockedAscendant));
        if (item.children) {
            item.children?.forEach(itemChild => {
                this._updateItem(itemChild);
            });
        }
    }
    _getAllLeafNodes(node) {
        if (node.children) {
            node.children?.forEach(childNode => {
                this._getAllLeafNodes(childNode);
            });
        }
        else {
            this._descendants.push(node);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SidenavComponent, deps: [{ token: i1.Router }, { token: i2.BreakpointObserver }, { token: i3.ResponsiveService }, { token: SidenavService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: SidenavComponent, isStandalone: true, selector: "centric-sidenav", inputs: { mode: "mode", opened: "opened", menuItems: "menuItems", scrollToTop: "scrollToTop", autoCollapse: "autoCollapse", autoFocus: "autoFocus", ariaLabelCloseButton: "ariaLabelCloseButton", fullOverMode: "fullOverMode" }, outputs: { openedChange: "openedChange" }, viewQueries: [{ propertyName: "sidenavContainer", first: true, predicate: MatSidenavContainer, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<mat-sidenav-container\r\n  autosize\r\n  class=\"centric-sidenav\"\r\n  (backdropClick)=\"toggleMenu()\">\r\n  <mat-sidenav\r\n    [class.full-over]=\"fullOverMode\"\r\n    [mode]=\"mode\"\r\n    [autoFocus]=\"autoFocus\"\r\n    [(opened)]=\"opened\"\r\n    (keydown.escape)=\"openedChange.emit(false)\">\r\n    <ng-content select=\"[sidenav-header]\"></ng-content>\r\n\r\n    <mat-nav-list>\r\n      @for (menuItem of menuItems; track menuItem) {\r\n        <centric-sidenav-item\r\n          [menuItem]=\"menuItem\"\r\n          [routerPath]=\"menuItem.path\"\r\n          [depth]=\"depth + 1\"\r\n          (expandedItemChange)=\"onExpandedItemChange($event)\"></centric-sidenav-item>\r\n      }\r\n    </mat-nav-list>\r\n\r\n    <ng-content select=\"[sidenav-footer]\"></ng-content>\r\n\r\n    <button\r\n      class=\"centric-sidenav-floating-close-button centric-button icon-high-emphasis-theme small\"\r\n      [class.cdk-visually-hidden]=\"!buttonCloseFocused\"\r\n      [attr.aria-label]=\"ariaLabelCloseButton\"\r\n      (focus)=\"onFocus()\"\r\n      (blur)=\"onBlur()\"\r\n      (click)=\"toggleMenu()\">\r\n      <div class=\"icon-plain\">\r\n        <centric-icon2\r\n          [svgIcon]=\"'cancel_b'\"\r\n          [size]=\"'20'\"></centric-icon2>\r\n      </div>\r\n    </button>\r\n  </mat-sidenav>\r\n  <mat-sidenav-content>\r\n    <ng-content></ng-content>\r\n  </mat-sidenav-content>\r\n</mat-sidenav-container>\r\n", dependencies: [{ kind: "component", type: SidenavItemComponent, selector: "centric-sidenav-item", inputs: ["menuItem", "routerPath", "depth"], outputs: ["expandedItemChange"] }, { kind: "component", type: MatNavList, selector: "mat-nav-list", exportAs: ["matNavList"] }, { kind: "component", type: MatSidenav, selector: "mat-sidenav", inputs: ["fixedInViewport", "fixedTopGap", "fixedBottomGap"], exportAs: ["matSidenav"] }, { kind: "component", type: MatSidenavContainer, selector: "mat-sidenav-container", exportAs: ["matSidenavContainer"] }, { kind: "component", type: MatSidenavContent, selector: "mat-sidenav-content" }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }], encapsulation: i0.ViewEncapsulation.None });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: SidenavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'centric-sidenav', encapsulation: ViewEncapsulation.None, imports: [SidenavItemComponent, ...materialComponents, CentricIcon2Component], standalone: true, template: "<mat-sidenav-container\r\n  autosize\r\n  class=\"centric-sidenav\"\r\n  (backdropClick)=\"toggleMenu()\">\r\n  <mat-sidenav\r\n    [class.full-over]=\"fullOverMode\"\r\n    [mode]=\"mode\"\r\n    [autoFocus]=\"autoFocus\"\r\n    [(opened)]=\"opened\"\r\n    (keydown.escape)=\"openedChange.emit(false)\">\r\n    <ng-content select=\"[sidenav-header]\"></ng-content>\r\n\r\n    <mat-nav-list>\r\n      @for (menuItem of menuItems; track menuItem) {\r\n        <centric-sidenav-item\r\n          [menuItem]=\"menuItem\"\r\n          [routerPath]=\"menuItem.path\"\r\n          [depth]=\"depth + 1\"\r\n          (expandedItemChange)=\"onExpandedItemChange($event)\"></centric-sidenav-item>\r\n      }\r\n    </mat-nav-list>\r\n\r\n    <ng-content select=\"[sidenav-footer]\"></ng-content>\r\n\r\n    <button\r\n      class=\"centric-sidenav-floating-close-button centric-button icon-high-emphasis-theme small\"\r\n      [class.cdk-visually-hidden]=\"!buttonCloseFocused\"\r\n      [attr.aria-label]=\"ariaLabelCloseButton\"\r\n      (focus)=\"onFocus()\"\r\n      (blur)=\"onBlur()\"\r\n      (click)=\"toggleMenu()\">\r\n      <div class=\"icon-plain\">\r\n        <centric-icon2\r\n          [svgIcon]=\"'cancel_b'\"\r\n          [size]=\"'20'\"></centric-icon2>\r\n      </div>\r\n    </button>\r\n  </mat-sidenav>\r\n  <mat-sidenav-content>\r\n    <ng-content></ng-content>\r\n  </mat-sidenav-content>\r\n</mat-sidenav-container>\r\n" }]
        }], ctorParameters: () => [{ type: i1.Router }, { type: i2.BreakpointObserver }, { type: i3.ResponsiveService }, { type: SidenavService }], propDecorators: { mode: [{
                type: Input
            }], opened: [{
                type: Input
            }], menuItems: [{
                type: Input
            }], scrollToTop: [{
                type: Input
            }], autoCollapse: [{
                type: Input
            }], autoFocus: [{
                type: Input
            }], ariaLabelCloseButton: [{
                type: Input
            }], fullOverMode: [{
                type: Input
            }], openedChange: [{
                type: Output
            }], sidenavContainer: [{
                type: ViewChild,
                args: [MatSidenavContainer]
            }] } });

const materialModules = [MatSidenavModule, MatListModule];
const windmillModules = [CentricButtonModule, CentricIcon2Module, WindmillTooltipModule];
const moduleComponents = [SidenavComponent, SidenavItemComponent];
class CentricSidenavModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricSidenavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricSidenavModule, imports: [CommonModule, LayoutModule, RouterModule, MatSidenavModule, MatListModule, CentricButtonModule, CentricIcon2Module, WindmillTooltipModule, SidenavComponent, SidenavItemComponent], exports: [SidenavComponent, SidenavItemComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricSidenavModule, imports: [CommonModule, LayoutModule, RouterModule, materialModules, windmillModules, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricSidenavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, LayoutModule, RouterModule, ...materialModules, ...windmillModules, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricSidenavModule, SidenavComponent, SidenavItemComponent };
